import * as Linking from 'expo-linking';
import React, { useCallback, useEffect, useRef } from 'react';

import {
  LinkingOptions,
  NavigationContainer,
  useNavigation
} from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';

import Theme from '../constants/Theme';
import { DeepLinkProvider } from '../context/DeepLinkContext';
import { LocalAuthenticationProvider } from '../context/LocalAuthenticationContext';
import { NotificationProvider } from '../context/NotificationContext';
import { useUserContext } from '../context/UserContext';
import NotFoundScreen from '../screens/NotFoundScreen';
import DrawerNavigator from './DrawerNavigator';
import { getScreenOptions } from './getScreenOptions';
import IntroNavigator from './IntroNavigator';
import ModalNavigator from './ModalNavigator';
import WelcomeNavigator from './WelcomeNavigator';
import { logAnalyticsScreenView } from '../common/analytics';
import { UserSessionProvider } from '../context/UserSessionContext';
import { setAppcuesScreen } from '../common/appcues';
import { routingInstrumentation } from '../common/sentry';
import api from '../api/api';

export type RootStackParamList = {
  DrawerNavigator: undefined;
  IntroNavigator: undefined;
  WelcomeNavigator: undefined;
  BottomTabNav: undefined;
  ModalNavigator: undefined;
  NotFound: undefined;
};

// you can add your newly created screen inside the config below.
export const linkingConfiguration: LinkingOptions<RootStackParamList> = {
  prefixes: [Linking.createURL('/')],
  config: {
    screens: {
      DrawerNavigator: {
        screens: {
          BottomTabNav: {
            screens: {
              DiscoverNav: {
                screens: {
                  Discover: 'discover',
                  DynamicScreen: 'dynamic-screen'
                }
              },
              /**
               * @deprecated
               */
              HomeNav: {
                screens: {
                  MyChloe: 'my-chloe'
                }
              },
              StudiesNav: {
                screens: {
                  MyStudies: 'my-studies',
                  StudyInfo: 'study',
                  StudyEdit: 'study-edit',
                  CreateStudy: 'create-study',
                  StudyEventForm: 'study-event-form',
                  FormTemplate: 'study-form-template',
                  FormTemplateModal: 'study-form-template-modal',
                  StudyComingSoon: 'study-coming-soon',
                  StudyGenerator: 'study-generator',
                  QualificationForm: 'study-qualification-form',
                  QualificationComplete: 'study-qualification-complete',
                  ScreenFailedMessage: 'screen-failed'
                }
              },
              RewardsNav: {
                screens: {
                  RewardsHubScreen: 'rewards-hub'
                }
              },
              MyDataNav: {
                screens: {
                  MyDataListScreen: 'my-data',
                  MyDataMeasurementDetailsScreen: 'my-data-measurement-detail',
                  WearableIntegrationScreen: 'wearable-integration'
                }
              },
              ToDoNav: {
                screens: {
                  ToDo: 'to-dos',
                  FormTemplate: 'to-do-form-template',
                  StudyEventForm: 'to-do-study-event-form',
                  ScreenFailedMessage: 'to-do-screen-failed'
                }
              },
              MessengerNav: {
                screens: {
                  MessengerRoomList: 'messenger',
                  MessengerRoom: 'messenger-room'
                }
              },
              MyProfileSettingsNav: {
                screens: {
                  MyProfileSettings: 'my-profile-settings',
                  MyProfileWearableSettings: 'my-profile-wearable-settings'
                }
              },
              ExamplesNav: {
                screens: {
                  ExampleScreen: 'examples',
                  CompareChartScreen: 'compare-chart',
                  DataTrackerChartScreen: 'data-tracker-chart',
                  GaugeChartScreen: 'gauge-chart',
                  CalendarScreen: 'calendar',
                  RollingAverageChartExampleScreen:
                    'rolling-average-chart-example',
                  CalendarDayScrollExampleScreen: 'calendar-day-scroll-example',
                  StudyJourneyChartScreen: 'study-journal-chart',
                  TodayEmptyStateScreen: 'today-empty-state',
                  ExampleButtonScreen: 'button-styles',
                  WellnessCategoryListScreen: 'wellness-category-list',
                  ExampleListScreen: 'list-styles',
                  StudyCardScreen: 'study-card',
                  ExampleHeaderTabViewScreen: 'example-header-tab',
                  ExampleWearableMeasurementsScreen: 'example-measurement-edit'
                }
              }
            }
          }
        }
      },
      WelcomeNavigator: {
        screens: {
          Welcome: 'welcome',
          WelcomeSurvey: 'welcome-survey'
        }
      },
      IntroNavigator: {
        screens: {
          Intro: 'intro',
          IntroAbout1Screen: 'intro-abount-1',
          IntroAbout2Screen: 'intro-abount-2',
          IntroAbout3Screen: 'intro-abount-3',
          IntroAbout4Screen: 'intro-abount-4',
          IntroAbout5Screen: 'intro-abount-5',
          LoginScreen: 'login',
          SignUpScreen: 'signup',
          SignUpEmailVerificationScreen: 'email-verification',
          ForgotPasswordScreen: 'forgot-password',
          ResetPasswordSentScreen: 'reset-password-sent'
        }
      },
      ModalNavigator: {
        screens: {
          FormTemplate: 'form-template-modal'
        }
      },
      NotFound: '*'
    }
  }
};

// A root stack navigator is often used for displaying modals on top of all other content
// Read more here: https://reactnavigation.org/docs/modal
const Stack = createStackNavigator<RootStackParamList>();

// Top-level routes that require an authenticated session
const PROTECTED_ROOT_ROUTES = ['DrawerNavigator', 'ModalNavigator'];

export default function Navigation() {
  // pass navigation to PushNotificationContext
  const navigationRef = useRef<ReturnType<typeof useNavigation<any>>>(null!);
  const { user } = useUserContext();
  const routeNameRef = useRef();

  const redirectToLoginIfUnauthenticated = useCallback(() => {
    if (user || api.accessToken) return;
    const rootState = navigationRef.current?.getRootState?.();
    if (!rootState) return;
    const topRouteName = rootState.routes[rootState.index]?.name;
    if (topRouteName && PROTECTED_ROOT_ROUTES.includes(topRouteName)) {
      (navigationRef.current as any)?.resetRoot?.({
        index: 0,
        routes: [
          {
            name: 'IntroNavigator',
            state: { index: 0, routes: [{ name: 'LoginScreen' }] }
          }
        ]
      });
    }
  }, [user]);

  useEffect(() => {
    redirectToLoginIfUnauthenticated();
  }, [redirectToLoginIfUnauthenticated]);

  return (
    <NavigationContainer
      ref={navigationRef as any}
      linking={linkingConfiguration}
      theme={Theme.navigationTheme}
      onReady={() => {
        if (routingInstrumentation) {
          routingInstrumentation.registerNavigationContainer(navigationRef);
        }
        redirectToLoginIfUnauthenticated();
        routeNameRef.current = navigationRef.current?.getCurrentRoute().name;
        if (navigationRef.current?.getCurrentRoute().name) {
          setAppcuesScreen(
            String(navigationRef.current?.getCurrentRoute().name)
          );
        }
      }}
      onStateChange={async () => {
        redirectToLoginIfUnauthenticated();
        const previousScreen = routeNameRef.current;
        const currentScreen = navigationRef.current?.getCurrentRoute().name;
        if (previousScreen !== currentScreen) {
          await logAnalyticsScreenView({
            screen_name: currentScreen,
            screen_class: currentScreen
          });
          setAppcuesScreen(String(currentScreen));
          // Analytics.logEvent('screen_view', { currentScreen });
        }
        // Save the current route name for later comparison
        routeNameRef.current = currentScreen;
      }}
    >
      <NotificationProvider navigation={navigationRef.current as any}>
        <UserSessionProvider navigation={navigationRef.current as any}>
          <LocalAuthenticationProvider
            navigation={navigationRef.current as any}
          >
            <DeepLinkProvider navigation={navigationRef.current as any}>
              <Stack.Navigator
                initialRouteName={user ? 'DrawerNavigator' : 'IntroNavigator'}
                screenOptions={({ route }) => {
                  return getScreenOptions(route);
                }}
              >
                <Stack.Screen
                  name='IntroNavigator'
                  component={IntroNavigator}
                />
                <Stack.Screen
                  name='WelcomeNavigator'
                  component={WelcomeNavigator}
                />
                <Stack.Screen
                  name='DrawerNavigator'
                  component={DrawerNavigator}
                />
                <Stack.Screen
                  name='NotFound'
                  component={NotFoundScreen}
                  options={{ title: 'Oops!' }}
                />
                <Stack.Screen
                  name='ModalNavigator'
                  component={ModalNavigator}
                />
              </Stack.Navigator>
            </DeepLinkProvider>
          </LocalAuthenticationProvider>
        </UserSessionProvider>
      </NotificationProvider>
    </NavigationContainer>
  );
}
